import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Upload, Plus, Eye, Download, Trash2, Search, Folder, User, LayoutGrid, List as ListIcon, Pencil } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { listDirectory, type DirectoryRow } from "@/lib/hr-extra";
import { useAuth } from "@/lib/auth-context";
import {
  listHrDocuments,
  uploadHrDocument,
  updateHrDocument,
  deleteHrDocument,
  getHrDocumentSignedUrl,
  fileTypeLabel,
  humanFileSize,
  docDisplayName,
  docTypeLabel,
  CATEGORY_LABELS,
  TYPES_BY_CATEGORY,
  type HrDocument,
  type DocCategory,
} from "@/lib/hr-documents";

// Categories visible in the Documents page (Asset Records is excluded — managed in Allot Assets)
const VISIBLE_CATEGORIES: DocCategory[] = ["employee_record", "policy_template", "salary_record", "compliance", "jd"];
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/documents")({
  component: DocumentsPage,
  head: () => ({ meta: [{ title: "Document Management — HR" }] }),
});

type Selection =
  | { kind: "all" }
  | { kind: "category"; category: DocCategory }
  | { kind: "employee"; employeeId: string };

function DocumentsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: docs = [] } = useQuery({ queryKey: ["hr-documents"], queryFn: listHrDocuments });
  const { data: people = [] } = useQuery<DirectoryRow[]>({ queryKey: ["hr-directory"], queryFn: listDirectory });

  const [selection, setSelection] = useState<Selection>({ kind: "all" });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    employee_record: true,
    policy_template: true,
  });
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [search, setSearch] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState<string>("all");
  const [docTypeFilter, setDocTypeFilter] = useState<string>("all");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [preview, setPreview] = useState<HrDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<HrDocument | null>(null);
  const [editDoc, setEditDoc] = useState<HrDocument | null>(null);

  // Stats
  const totalDocs = docs.length;
  const empFolders = new Set(docs.filter((d) => d.employee_id).map((d) => d.employee_id)).size;
  const policyDocs = docs.filter((d) => d.doc_category === "policy_template").length;

  // Counts per category/employee
  const categoryDocs = (cat: DocCategory) => docs.filter((d) => d.doc_category === cat);

  // Filter docs based on selection + search + filters
  const visibleDocs = useMemo(() => {
    let list = docs.filter((d) => d.doc_category !== "asset_record");
    if (selection.kind === "category") list = list.filter((d) => d.doc_category === selection.category);
    if (selection.kind === "employee") list = list.filter((d) => d.employee_id === selection.employeeId && d.doc_type !== "asset_acknowledgment");
    if (fileTypeFilter !== "all") list = list.filter((d) => d.file_type === fileTypeFilter);
    if (docTypeFilter !== "all") list = list.filter((d) => d.doc_type === docTypeFilter);
    const s = search.toLowerCase().trim();
    if (s) list = list.filter((d) => docDisplayName(d).toLowerCase().includes(s) || d.file_name.toLowerCase().includes(s));
    return list;
  }, [docs, selection, search, fileTypeFilter, docTypeFilter]);

  // Sorted employees for tree
  const sortedPeople = useMemo(
    () => [...people].sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [people]
  );
  const employeesForTree = showAllEmployees ? sortedPeople : sortedPeople.slice(0, 5);
  const employeeNameById = (id: string) => people.find((p) => p.id === id)?.full_name ?? "Unknown";

  // Breadcrumb
  const breadcrumb = useMemo(() => {
    const parts: { label: string; sel: Selection }[] = [{ label: "All Documents", sel: { kind: "all" } }];
    if (selection.kind === "category") parts.push({ label: CATEGORY_LABELS[selection.category], sel: selection });
    if (selection.kind === "employee") {
      parts.push({ label: "Employee Records", sel: { kind: "category", category: "employee_record" } });
      parts.push({ label: employeeNameById(selection.employeeId), sel: selection });
    }
    return parts;
  }, [selection, people]);

  const del = useMutation({
    mutationFn: (d: HrDocument) => deleteHrDocument(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["hr-documents"] }); qc.invalidateQueries({ queryKey: ["emp-docs"] }); toast.success("Deleted"); setConfirmDel(null); },
    onError: (e: Error) => toast.error(e.message),
  });

  const openPreview = async (d: HrDocument) => {
    if (d.file_path.startsWith("__virtual__")) { toast.info("Auto-generated record; no file attached"); return; }
    try {
      const url = await getHrDocumentSignedUrl(d.file_path);
      setPreview(d); setPreviewUrl(url);
    } catch (e) { toast.error((e as Error).message); }
  };

  const downloadDoc = async (d: HrDocument) => {
    if (d.file_path.startsWith("__virtual__")) { toast.info("Auto-generated record; no file to download"); return; }
    try {
      const url = await getHrDocumentSignedUrl(d.file_path);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b bg-background">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Document Management</h1>
            <p className="text-xs text-muted-foreground mt-1">
              {totalDocs} documents · {empFolders} employee folders · {policyDocs} policy docs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setUploadOpen(true)} className="bg-black text-white hover:bg-black/90 h-9">
              <Upload className="h-4 w-4 mr-1.5" /> Upload Document
            </Button>
            <Button variant="outline" onClick={() => setFolderOpen(true)} className="h-9">
              <Plus className="h-4 w-4 mr-1.5" /> New Folder
            </Button>
          </div>
        </div>
        {/* Breadcrumb */}
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              <button
                onClick={() => setSelection(b.sel)}
                className={i === breadcrumb.length - 1 ? "font-semibold text-foreground" : "hover:text-foreground"}
              >
                {b.label}
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Two panels */}
      <div className="flex flex-1 min-h-0">
        {/* Left tree */}
        <aside className="w-[280px] shrink-0 border-r bg-card overflow-y-auto p-3">
          <TreeFolder
            label="Employee Records"
            count={categoryDocs("employee_record").length}
            expanded={!!expanded.employee_record}
            onToggle={() => setExpanded((p) => ({ ...p, employee_record: !p.employee_record }))}
            onSelect={() => setSelection({ kind: "category", category: "employee_record" })}
            selected={selection.kind === "category" && selection.category === "employee_record"}
          >
            {employeesForTree.map((p) => (
              <TreeItem
                key={p.id}
                icon={<User className="h-3.5 w-3.5" />}
                label={p.full_name}
                selected={selection.kind === "employee" && selection.employeeId === p.id}
                onClick={() => setSelection({ kind: "employee", employeeId: p.id })}
              />
            ))}
            {sortedPeople.length > 5 && (
              <button
                onClick={() => setShowAllEmployees((v) => !v)}
                className="text-[11px] text-muted-foreground hover:text-foreground pl-7 py-1"
              >
                {showAllEmployees ? "Show less" : `... ${sortedPeople.length - 5} more`}
              </button>
            )}
          </TreeFolder>

          <TreeFolder
            label="Policies & Templates"
            count={categoryDocs("policy_template").length}
            expanded={!!expanded.policy_template}
            onToggle={() => setExpanded((p) => ({ ...p, policy_template: !p.policy_template }))}
            onSelect={() => setSelection({ kind: "category", category: "policy_template" })}
            selected={selection.kind === "category" && selection.category === "policy_template"}
          >
            {categoryDocs("policy_template").map((d) => (
              <TreeItem
                key={d.id}
                icon={<FileBadge type={d.file_type} />}
                label={docDisplayName(d)}
                onClick={() => openPreview(d)}
              />
            ))}
          </TreeFolder>



          <TreeFolder
            label="Salary Records"
            count={categoryDocs("salary_record").length}
            expanded={!!expanded.salary_record}
            onToggle={() => setExpanded((p) => ({ ...p, salary_record: !p.salary_record }))}
            onSelect={() => setSelection({ kind: "category", category: "salary_record" })}
            selected={selection.kind === "category" && selection.category === "salary_record"}
          />

          <TreeFolder
            label="Compliance Documents"
            count={categoryDocs("compliance").length}
            expanded={!!expanded.compliance}
            onToggle={() => setExpanded((p) => ({ ...p, compliance: !p.compliance }))}
            onSelect={() => setSelection({ kind: "category", category: "compliance" })}
            selected={selection.kind === "category" && selection.category === "compliance"}
          />

          <TreeFolder
            label="JD (Job Description)"
            count={categoryDocs("jd").length}
            expanded={!!expanded.jd}
            onToggle={() => setExpanded((p) => ({ ...p, jd: !p.jd }))}
            onSelect={() => setSelection({ kind: "category", category: "jd" })}
            selected={selection.kind === "category" && selection.category === "jd"}
          />
        </aside>

        {/* Right grid */}
        <main className="flex-1 overflow-y-auto p-5">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
              <SelectTrigger className="h-9 w-32 text-xs"><SelectValue placeholder="All Files" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Files</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="docx">Word</SelectItem>
                <SelectItem value="xlsx">Excel</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
              <SelectTrigger className="h-9 w-40 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Array.from(new Set(Object.values(TYPES_BY_CATEGORY).flat().map((t) => t.value))).map((v) => {
                  const lbl = Object.values(TYPES_BY_CATEGORY).flat().find((t) => t.value === v)?.label ?? v;
                  return <SelectItem key={v} value={v}>{lbl}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 text-xs"
              />
            </div>
            <div className="flex items-center rounded-md border overflow-hidden ml-auto">
              <button
                onClick={() => setView("grid")}
                className={`h-9 w-9 flex items-center justify-center ${view === "grid" ? "bg-muted" : ""}`}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView("list")}
                className={`h-9 w-9 flex items-center justify-center border-l ${view === "list" ? "bg-muted" : ""}`}
                aria-label="List view"
              >
                <ListIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {visibleDocs.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-12">No documents found</div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {visibleDocs.map((d) => (
                <DocCard
                  key={d.id}
                  doc={d}
                  onView={() => openPreview(d)}
                  onDownload={() => downloadDoc(d)}
                  onEdit={() => setEditDoc(d)}
                  onDelete={() => setConfirmDel(d)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-md border bg-card overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[11px] uppercase font-semibold text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Date Uploaded</th>
                    <th className="text-left px-3 py-2">Size</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDocs.map((d) => (
                    <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">{docDisplayName(d)}{docTypeLabel(d) ? <span className="text-muted-foreground font-normal"> ({docTypeLabel(d)})</span> : null}</td>
                      <td className="px-3 py-2">{d.doc_type ? (TYPES_BY_CATEGORY[d.doc_category]?.find((t) => t.value === d.doc_type)?.label ?? d.doc_type) : "—"}</td>
                      <td className="px-3 py-2">{d.employee_id ? employeeNameById(d.employee_id) : "—"}</td>
                      <td className="px-3 py-2">{formatIst(d.created_at, "dd MMM yyyy")}</td>
                      <td className="px-3 py-2">{humanFileSize(d.file_size)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openPreview(d)} className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted" title="View"><Eye className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setEditDoc(d)} className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => downloadDoc(d)} className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted" title="Download"><Download className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setConfirmDel(d)} className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-destructive" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      <UploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        people={sortedPeople}
        defaultEmployeeId={selection.kind === "employee" ? selection.employeeId : null}
        defaultCategory={selection.kind === "category" ? selection.category : selection.kind === "employee" ? "employee_record" : null}
        uploadedBy={user?.id ?? null}
        onDone={() => qc.invalidateQueries({ queryKey: ["hr-documents"] })}
      />

      <NewFolderModal open={folderOpen} onOpenChange={setFolderOpen} />

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) { setPreview(null); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="text-sm">{preview ? docDisplayName(preview) : ""}</DialogTitle></DialogHeader>
          {previewUrl && preview && (
            preview.file_type === "image" ? (
              <img src={previewUrl} alt={preview.file_name} className="max-h-[70vh] mx-auto" />
            ) : preview.file_type === "pdf" ? (
              <iframe src={previewUrl} className="w-full h-[70vh] rounded border" title={preview.file_name} />
            ) : (
              <div className="py-8 text-center text-sm">
                Preview not available. <a className="text-primary underline" href={previewUrl} target="_blank" rel="noopener">Open in new tab</a>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Delete document?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Delete {confirmDel ? docDisplayName(confirmDel) : ""}? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" disabled={del.isPending} onClick={() => confirmDel && del.mutate(confirmDel)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditDocumentModal
        doc={editDoc}
        onClose={() => setEditDoc(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ["hr-documents"] })}
      />
    </div>
  );
}

function TreeFolder({
  label, count, expanded, onToggle, onSelect, selected, children,
}: {
  label: string; count: number; expanded: boolean;
  onToggle: () => void; onSelect: () => void; selected?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-1">
      <div
        className={`flex items-center gap-1 rounded px-1.5 py-1.5 cursor-pointer text-xs ${selected ? "bg-[#F0F4FF] font-semibold" : "hover:bg-muted"}`}
        onClick={onSelect}
      >
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="p-0.5">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <Folder className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="flex-1 truncate">{label}</span>
        <span className="text-[10px] text-muted-foreground">{count}</span>
      </div>
      {expanded && children && <div className="ml-4 mt-0.5 space-y-0.5">{children}</div>}
    </div>
  );
}

function TreeItem({ icon, label, onClick, selected }: { icon: React.ReactNode; label: string; onClick: () => void; selected?: boolean }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer ${selected ? "bg-[#F0F4FF] font-semibold" : "hover:bg-muted"}`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
}

function FileBadge({ type }: { type: string | null }) {
  const f = fileTypeLabel(type);
  return (
    <span style={{ background: f.bg, color: f.fg }} className="inline-flex h-4 min-w-[26px] items-center justify-center rounded text-[9px] font-bold px-1">
      {f.label}
    </span>
  );
}

function DocCard({ doc, onView, onDownload, onDelete, onEdit }: { doc: HrDocument; onView: () => void; onDownload: () => void; onDelete: () => void; onEdit: () => void }) {
  const f = fileTypeLabel(doc.file_type);
  const typeLbl = docTypeLabel(doc);
  return (
    <div className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition">
      <div className="flex flex-col items-center text-center">
        <div
          style={{ background: f.bg, color: f.fg }}
          className="w-16 h-20 rounded flex items-center justify-center font-bold text-sm mb-3"
        >
          {f.label}
        </div>
        <div className="text-xs font-medium truncate w-full" title={doc.file_name}>{docDisplayName(doc)}</div>
        {typeLbl && <div className="text-[11px] text-muted-foreground mt-0.5">({typeLbl})</div>}
        <div className="text-[11px] text-muted-foreground mt-1">{formatIst(doc.created_at, "dd MMM yyyy")}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{humanFileSize(doc.file_size)}</div>
      </div>
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition rounded-lg flex items-center justify-center gap-2">
        <button onClick={onView} className="h-9 w-9 rounded-md bg-white text-black flex items-center justify-center hover:bg-white/90" title="View"><Eye className="h-4 w-4" /></button>
        <button onClick={onEdit} className="h-9 w-9 rounded-md bg-white text-black flex items-center justify-center hover:bg-white/90" title="Edit"><Pencil className="h-4 w-4" /></button>
        <button onClick={onDownload} className="h-9 w-9 rounded-md bg-white text-black flex items-center justify-center hover:bg-white/90" title="Download"><Download className="h-4 w-4" /></button>
        <button onClick={onDelete} className="h-9 w-9 rounded-md bg-white text-destructive flex items-center justify-center hover:bg-white/90" title="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function UploadModal({
  open, onOpenChange, people, defaultEmployeeId, defaultCategory, uploadedBy, onDone,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  people: DirectoryRow[]; defaultEmployeeId: string | null; defaultCategory: DocCategory | null;
  uploadedBy: string | null; onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState<DocCategory>(defaultCategory ?? "employee_record");
  const [employeeId, setEmployeeId] = useState<string | null>(defaultEmployeeId);
  const [docType, setDocType] = useState<string>("");
  const [notes, setNotes] = useState("");

  // Sync defaults when opened
  const handleOpen = (v: boolean) => {
    if (v) {
      setFile(null); setNotes(""); setDisplayName("");
      setCategory(defaultCategory ?? "employee_record");
      setEmployeeId(defaultEmployeeId);
      setDocType("");
    }
    onOpenChange(v);
  };

  const up = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a file");
      if (!docType) throw new Error("Select document type");
      if (category === "employee_record" && !employeeId) throw new Error("Select an employee");
      await uploadHrDocument({
        file, category, doc_type: docType,
        display_name: displayName,
        employee_id: category === "employee_record" ? employeeId : null,
        uploaded_by: uploadedBy, notes,
      });
    },
    onSuccess: () => { toast.success("Document uploaded successfully"); onDone(); onOpenChange(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-sm">Upload Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Document Name</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Aadhaar Card (Front)"
              className="h-9 text-xs mt-1"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Optional. Shown as the document title.</p>
          </div>
          <div>
            <label className="text-xs font-medium">File *</label>
            <Input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="h-9 text-xs mt-1"
            />
            {file && <p className="text-[11px] text-muted-foreground mt-1">{file.name} · {humanFileSize(file.size)}</p>}
          </div>
          <div>
            <label className="text-xs font-medium">Document Category *</label>
            <Select value={category} onValueChange={(v) => { setCategory(v as DocCategory); setDocType(""); }}>
              <SelectTrigger className="h-9 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VISIBLE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {category === "employee_record" && (
            <div>
              <label className="text-xs font-medium">Employee *</label>
              <Select value={employeeId ?? ""} onValueChange={(v) => setEmployeeId(v)}>
                <SelectTrigger className="h-9 text-xs mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name} {p.employee_id ? `· #${p.employee_id}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium">Document Type *</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-9 text-xs mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {TYPES_BY_CATEGORY[category].map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Notes</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-xs mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="bg-black text-white hover:bg-black/90" disabled={up.isPending} onClick={() => up.mutate()}>
            {up.isPending ? "Uploading…" : "Upload →"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFolderModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<DocCategory>("employee_record");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="text-sm">New Folder</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Folder Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-xs mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium">Category *</label>
            <Select value={category} onValueChange={(v) => setCategory(v as DocCategory)}>
              <SelectTrigger className="h-9 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VISIBLE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={() => { if (!name.trim()) { toast.error("Folder name required"); return; } toast.success(`Folder "${name}" added under ${CATEGORY_LABELS[category]}`); setName(""); onOpenChange(false); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDocumentModal({ doc, onClose, onDone }: { doc: HrDocument | null; onClose: () => void; onDone: () => void }) {
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState<DocCategory>("employee_record");
  const [docType, setDocType] = useState<string>("");

  // Reset fields whenever the doc changes
  useEffect(() => {
    if (doc) {
      setDisplayName(doc.display_name ?? "");
      setCategory(doc.doc_category);
      setDocType(doc.doc_type ?? "");
    }
  }, [doc?.id]);

  const save = useMutation({
    mutationFn: async () => {
      if (!doc) return;
      if (!docType) throw new Error("Select document type");
      await updateHrDocument(doc.id, {
        display_name: displayName,
        doc_category: category,
        doc_type: docType,
      });
    },
    onSuccess: () => { toast.success("Document updated"); onDone(); onClose(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-sm">Edit Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Document Name</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Aadhaar Card (Front)"
              className="h-9 text-xs mt-1"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Shown as the document title.</p>
          </div>
          <div>
            <label className="text-xs font-medium">Document Category *</label>
            <Select value={category} onValueChange={(v) => { setCategory(v as DocCategory); setDocType(""); }}>
              <SelectTrigger className="h-9 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VISIBLE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Document Type *</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-9 text-xs mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {TYPES_BY_CATEGORY[category].map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-black text-white hover:bg-black/90" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
